import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircleIcon, ClockCountdownIcon, WarningIcon } from "@phosphor-icons/react";
import { useActivation } from "@/contexts/ActivationContext";

/**
 * Post-Stripe-checkout landing. Polls /payments/status/{session_id} until paid.
 * On timeout, still shows a link back to Settings → Billing where the source of
 * truth (webhook-synced) can be seen.
 */
export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);
  const { refresh: refreshActivation } = useActivation();

  useEffect(() => {
    if (!sessionId) { setStatus("no-session"); return; }
    let cancelled = false;
    const poll = async () => {
      for (let i = 0; i < 30 && !cancelled; i++) {
        setAttempts(i + 1);
        try {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid" || data.status === "completed") {
            if (!cancelled) {
              setStatus("paid");
              // Refresh activation status so the gate lifts when they return
              refreshActivation();
            }
            return;
          }
        } catch (e) { /* keep polling */ }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setStatus("timeout");
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refreshActivation]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8" data-testid="billing-success-page">
      <div className="bg-white border border-slate-300 rounded-sm p-10 max-w-md w-full text-center">
        {status === "polling" && (
          <>
            <ClockCountdownIcon size={48} weight="fill" className="mx-auto text-blue-600 mb-3 animate-pulse" />
            <div className="section-eyebrow text-blue-700 mb-1">/// Processing</div>
            <div className="font-display text-xl font-black mb-2">Confirming your subscription…</div>
            <div className="text-sm text-slate-600">Attempt {attempts}/30 · this usually takes a few seconds</div>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircleIcon size={56} weight="fill" className="mx-auto text-emerald-600 mb-3" />
            <div className="section-eyebrow text-emerald-700 mb-1">/// You&apos;re in</div>
            <div className="font-display text-2xl font-black mb-2">Subscription active</div>
            <div className="text-sm text-slate-600 mb-5">
              Your 14-day free trial has started. We&apos;ll only charge your card when the trial ends —
              cancel anytime before then and pay nothing.
            </div>
            <Link to="/settings"><Button className="rounded-sm w-full" data-testid="billing-success-back">Back to Settings</Button></Link>
          </>
        )}
        {status === "timeout" && (
          <>
            <WarningIcon size={48} weight="fill" className="mx-auto text-amber-600 mb-3" />
            <div className="section-eyebrow text-amber-700 mb-1">/// Still processing</div>
            <div className="font-display text-xl font-black mb-2">Taking longer than usual</div>
            <div className="text-sm text-slate-600 mb-5">
              Your payment is being processed. Check Settings → Billing in a moment to see the latest status.
            </div>
            <Link to="/settings"><Button className="rounded-sm w-full">Go to Settings</Button></Link>
          </>
        )}
        {status === "no-session" && (
          <>
            <WarningIcon size={48} weight="fill" className="mx-auto text-red-600 mb-3" />
            <div className="font-display text-xl font-black mb-2">Missing session</div>
            <div className="text-sm text-slate-600 mb-5">We couldn&apos;t find a checkout session. Please try again from Settings.</div>
            <Link to="/settings"><Button className="rounded-sm w-full">Back to Settings</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
